/**
 * Theme hook for managing dark/light mode with persistence
 * Integrates with settingsStore for cross-platform consistency
 */

import { useEffect, useState, useCallback } from 'react';
import { useSettingsStore } from '../stores/settingsStore';

type ThemeMode = 'light' | 'dark' | 'system';

interface UseThemeReturn {
  theme: ThemeMode;
  isDarkMode: boolean;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

/**
 * Applies the dark class to document.documentElement based on the resolved theme
 */
function applyTheme(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Resolves the actual dark/light mode based on theme setting
 * If 'system', uses OS preference
 */
function resolveIsDark(theme: ThemeMode): boolean {
  if (theme === 'system') {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }
  return theme === 'dark';
}

/**
 * Hook for managing dark/light theme with persistence
 *
 * Usage:
 * ```tsx
 * const { isDarkMode, toggleTheme, theme, setTheme } = useTheme();
 * ```
 */
export function useTheme(): UseThemeReturn {
  const { theme, setTheme: setStoreTheme } = useSettingsStore();
  const [isDarkMode, setIsDarkMode] = useState(() => resolveIsDark(theme));

  // Apply theme whenever it changes
  useEffect(() => {
    const resolved = resolveIsDark(theme);
    setIsDarkMode(resolved);
    applyTheme(resolved);
  }, [theme]);

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
      applyTheme(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Toggle between light and dark (not system)
  const toggleTheme = useCallback(() => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setStoreTheme(newTheme);
  }, [isDarkMode, setStoreTheme]);

  // Set specific theme
  const setTheme = useCallback((newTheme: ThemeMode) => {
    setStoreTheme(newTheme);
  }, [setStoreTheme]);

  return {
    theme,
    isDarkMode,
    setTheme,
    toggleTheme,
  };
}

/**
 * Initialize theme on app startup
 * Call this once at the app root level
 */
export function initializeTheme() {
  // Get persisted theme from localStorage
  const stored = localStorage.getItem('agentic-ivy-settings');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const theme = parsed.state?.theme || 'system';
      const isDark = resolveIsDark(theme);
      applyTheme(isDark);
    } catch {
      // If parsing fails, default to system preference
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      applyTheme(isDark);
    }
  } else {
    // No stored preference, use system
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    applyTheme(isDark);
  }
}
